import React from 'react';

import {
  DescriptionList,
  Link,
  DescriptionItem,
  TypographyListItem,
  Grid,
  Typography,
  Stack,
  Help,
} from '@gogame/components';
import { AmountFormatter, DateRangeFormatter } from '@gogame/intl-ts';

import Importance from 'scenes/FinancialResults/components/Importance';
import Warnings from 'scenes/FinancialResults/components/Warnings';
const GeneralSection = ({
  data,
  onOpenBusinessPartner,
  onOpenAffiliate,
  onExportAttachment,
  onOpenIo,
}) => {
  const budgetOverrun =
    data?.totalInvoicesAmount?.amount > data?.budget?.amount
      ? data?.totalInvoicesAmount?.amount - data?.budget?.amount
      : 0;
  const isAffiliateBusinessEntity = !!data?.affiliates?.length;

  return (
    <DescriptionList disableGutters={true} data={data} stripEmpty={true}>
      <DescriptionItem
        label={'Period'}
        type={'dateRange'}
        field={'period'}
        template={'MONTH'}
        timeZone={'utc'}
      />
      <DescriptionItem
        label={'Due date'}
        type={'date'}
        template={'LL'}
        timeZone={'local'}
        field={'dueDate'}
      />
      <DescriptionItem
        label={'Business partner'}
        field={'businessPartner'}
        type={'link'}
        linkLabelField={'name'}
        onValueClick={onOpenBusinessPartner}
      />
      {isAffiliateBusinessEntity ? (
        <DescriptionItem
          label={'Affiliates'}
          field={'affiliates'}
          type={'typographyList'}
          unitsLabel={'Affiliates'}
          limit={1}
          item={({ id, name, warnings }) => {
            return (
              <Warnings warnings={warnings}>
                <Link
                  /* eslint-disable-next-line */
                  data-cy-type={'link'}
                  onClick={(_) => onOpenAffiliate(_, { id })}
                >
                  <TypographyListItem>{name}</TypographyListItem>
                </Link>
              </Warnings>
            );
          }}
        />
      ) : null}
      <DescriptionItem
        type={'text'}
        label={'Business Partner County'}
        field={'businessPartnerCountry.name'}
      />
      <DescriptionItem
        placeholder={'—'}
        type={'text'}
        label={'Customer company'}
        field={'customerCompany.name'}
      />
      <DescriptionItem
        label={'Applications'}
        field={'affectedApplications'}
        type={'text'}
        valueParser={'commaList'}
      />
      <DescriptionItem
        type={'text'}
        label={'Expense type'}
        field={'expenseType.name'}
      />
      <DescriptionItem
        type={'text'}
        label={'Currency'}
        field={'currencyCode'}
        valueParser={(v) => v.toUpperCase()}
      />
      <DescriptionItem
        label={'Importance'}
        type={({ value: { id, name }, ...rest }) => {
          return (
            /* eslint-disable-next-line */
            <Importance data-cy-role={rest['data-cy-role']} id={id}>
              {name}
            </Importance>
          );
        }}
        field={'importance'}
      />
      <DescriptionItem
        label={'Total invoices amount'}
        type={({ value }) => {
          if (!value) {
            return null;
          }
          return (
            <Grid container>
              <Grid item xs={12}>
                <Typography>
                  <AmountFormatter {...value.totalInvoicesAmount} />
                </Typography>
              </Grid>
              {value.ioDiscrepancyDetails ? (
                <Grid item xs={12}>
                  <Typography variant={'caption'} color={'error'}>
                    {value.ioDiscrepancyDetails.message}
                  </Typography>
                </Grid>
              ) : (
                !!value.budgetOverrun &&
                value.hasIoSpend &&
                !value.skipSpendCheck && (
                  <Grid item xs={12}>
                    <Typography variant={'caption'} color={'error'}>
                      <AmountFormatter
                        amount={value.budgetOverrun}
                        currencyCode={data?.budget.currencyCode}
                      />{' '}
                      more than monthly budget
                    </Typography>
                  </Grid>
                )
              )}
            </Grid>
          );
        }}
        value={
          data?.spend
            ? {
                totalInvoicesAmount: data?.totalInvoicesAmount,
                hasIoSpend: !!data?.spend?.io.length,
                skipSpendCheck: data?.spend?.skipSpendCheck,
                budgetOverrun,
                ioDiscrepancyDetails: data?.spend?.ioDiscrepancyDetails,
              }
            : null
        }
      />
      <DescriptionItem
        label={
          <Stack direction={'row'} spacing={1}>
            <Stack>Spend</Stack>
            <Help>
              Spend for the activity covered by this FR as it was at the moment
              of the financial result creation
            </Help>
          </Stack>
        }
        itemProps={{
          'data-cy-label': 'Spend',
        }}
        type={'amount'}
        field={'spend.spend'}
      />
      <DescriptionItem
        label={
          <Stack direction={'row'} spacing={1}>
            <Stack>Original spend</Stack>
            <Help>
              Original spend before any changes were made to it - e.g. changes
              of cost model rate, spend adjustments, etc.
            </Help>
          </Stack>
        }
        itemProps={{
          'data-cy-label': 'Original spend',
        }}
        field={'spend.originalSpend'}
        type={'amount'}
      />
      <DescriptionItem
        label={'Total spend adjustments amount'}
        field={'totalSpendAdjustmentsAmount'}
        type={'amount'}
      />
      <DescriptionItem
        label={'Payment term'}
        field={'spend.paymentTerm'}
        type={'text'}
      />
      <DescriptionItem
        label={'Description'}
        type={'text'}
        field={'description'}
      />
      <DescriptionItem
        label={'Attachments'}
        field={'attachments'}
        type={'typographyList'}
        item={({ id, name }) => {
          return (
            <Link
              /* eslint-disable-next-line */
              data-cy-type={'link'}
              onClick={() => onExportAttachment(id)}
            >
              <TypographyListItem>{name}</TypographyListItem>
            </Link>
          );
        }}
      />
      <DescriptionItem
        label={'Payment date'}
        type={'date'}
        template={'LLL'}
        timeZone={'local'}
        field={'paymentDate'}
      />
      <DescriptionItem
        label={'IO Budget for FR period'}
        type={'amount'}
        field={'ioBudgetForFr'}
      />
      <DescriptionItem
        label={'IO Period'}
        type={'typographyList'}
        field={'ioDocumentPeriods'}
        item={({ id, period }) => {
          const { from, to } = period;

          return (
            <Link onClick={(_) => onOpenIo(_, { id })}>
              <Typography>
                {' '}
                <DateRangeFormatter value={[from, to]} template={'MONTH'} />
              </Typography>
            </Link>
          );
        }}
      />
    </DescriptionList>
  );
};

export default GeneralSection;
